// The import-job record — a FAITHFUL transcription of SPEC §3.1's field table
// and §3.2's state machine.
//
// docs/initiatives/new/dashboard-import-tool/SPEC.md §3.1 (field table) and §3.2
// (12-state enum) are both marked **spec-final**. This file is the typed
// contract four parallel Milestone tracks (B, C, D, E1) code against, so it
// transcribes the SPEC rather than improving on it: no invented fields, no
// renamed fields, no "nicer" shapes. If a shape here looks wrong, the fix is a
// SPEC edit first (the plan-is-wrong protocol), not a divergence here.
//
// OPTIONALITY IS DERIVED FROM THE JOB LIFECYCLE, not from taste. A job is
// created in STAGED carrying only what the create call knows; every other
// field is written by a later stage and is therefore absent until that stage
// runs. Each optional field below cites the state that populates it. Getting
// this wrong in the other direction — marking a late field required — would
// force every consumer to fabricate a value at create time.

import { z } from 'zod';

import { mappingConfigSchema } from './mapping.js';
import { sourceFileHintSchema } from './fields.js';

// ---------------------------------------------------------------------------
// §3.1 — table keys
// ---------------------------------------------------------------------------

/**
 * SPEC §3.1: "PK `jobId`, SK `recordType` — the job record itself is
 * `SK = "JOB"`; each batch step writes one immutable batch manifest item
 * (`SK = "BATCH#00042"`)."
 *
 * Both schemas below carry `recordType` as a real field, so a Query over the
 * jobId partition can discriminate job-vs-manifest on the returned items (and
 * so parsing an item never strips its sort key). The SPEC's only batch example
 * is `BATCH#00042` — it states no padding RULE, but the padding is load-bearing
 * regardless: rollback and reversal read manifests by Query, and DynamoDB sorts
 * sort keys lexicographically, so `BATCH#9` would sort before `BATCH#10`.
 * The manifest schema therefore pins the example's 5-digit shape.
 */
export const JOB_RECORD_TYPE = 'JOB' as const;
export const BATCH_RECORD_TYPE_PREFIX = 'BATCH#' as const;

// ---------------------------------------------------------------------------
// §3.2 — the 13-state status enum (FROZEN)
// ---------------------------------------------------------------------------

/**
 * SPEC §3.2's thirteen states, in the SPEC's own order. (CLEANED_UP — the
 * operator-only exit from FAILED_NEEDS_CLEANUP — was added by the 2026-08-10
 * write-path adversarial review, via the SPEC-first protocol this file's
 * header describes.)
 *
 * This list is duplicated in `backends/imports/src/domain/jobs/
 * import-job-state-machine.ts` (IMPORT_JOB_STATUSES) because the domain ring
 * may not depend on a contracts package it doesn't own, and this contract may
 * not depend on a backend. The duplication is deliberate and guarded by a
 * conformance test in that backend asserting the two lists are identical —
 * see `__tests__/domain/import-job-contract-conformance.test.ts`. If you edit
 * one list, that test fails until you edit the other.
 */
export const IMPORT_JOB_STATUSES = [
  'STAGED',
  'MAPPING',
  'VALIDATING',
  'READY',
  'COMMITTING',
  'CANCELLING',
  'SUCCESS',
  'FAILED',
  'FAILED_NEEDS_CLEANUP',
  'CLEANED_UP',
  'CANCELLED',
  'REVERSING',
  'REVERSED',
] as const;

export const importJobStatusSchema = z.enum(IMPORT_JOB_STATUSES);
export type ImportJobStatus = z.infer<typeof importJobStatusSchema>;

/**
 * SPEC §3.2: the states in which the Inngest workflow (or a reversal) is
 * actively running, i.e. the only states worth polling. SPEC §6.2: "polls
 * **only while a job is in a non-terminal working state** (COMMITTING /
 * CANCELLING / REVERSING) and stops on terminal states — no idle polling."
 *
 * Exported so E2's poller and E1's badge map read the same list instead of
 * each hardcoding three strings.
 */
export const IMPORT_JOB_WORKING_STATUSES = [
  'COMMITTING',
  'CANCELLING',
  'REVERSING',
] as const satisfies readonly ImportJobStatus[];

/**
 * The statuses with no outbound transition — DERIVED from §3.2's transition
 * table (they appear only in its "To" column), not quoted from it: §3.2 has no
 * sentence labelling them "terminal". The `backends/imports` conformance test
 * recomputes this list from the domain transition map and fails on divergence,
 * which is what makes the derivation trustworthy rather than asserted.
 *
 * NOTE — SUCCESS and FAILED_NEEDS_CLEANUP are deliberately NOT here. §3.2
 * defines `SUCCESS → REVERSING` and `FAILED_NEEDS_CLEANUP → CLEANED_UP` (the
 * operator-only cleanup confirmation), so both have an outbound transition and
 * are not terminal in the state machine's sense. The conformance test in
 * `backends/imports` derives this list from the domain transition map and
 * fails if the two disagree — it caught exactly that mistake once already.
 *
 * If what you want is "should the Hub stop polling this job?", that is
 * `isImportJobSettled` below, NOT this list. The two questions differ precisely
 * at SUCCESS and FAILED_NEEDS_CLEANUP: nothing further happens unattended
 * (stop polling), but a user or operator may still move them (not terminal).
 */
export const IMPORT_JOB_TERMINAL_STATUSES = [
  'FAILED',
  'CLEANED_UP',
  'CANCELLED',
  'REVERSED',
] as const satisfies readonly ImportJobStatus[];

/**
 * Whether a job has stopped changing on its own — i.e. whether the §6.2 poller
 * should stop.
 *
 * SPEC §6.2: the poller "polls **only while a job is in a non-terminal working
 * state** (COMMITTING / CANCELLING / REVERSING) and stops on terminal states —
 * no idle polling." That is the complement of the working set, so it is derived
 * from it rather than maintained as a third hand-written list. SUCCESS is
 * settled by this definition even though it is not terminal above.
 */
export function isImportJobSettled(status: ImportJobStatus): boolean {
  return !(IMPORT_JOB_WORKING_STATUSES as readonly ImportJobStatus[]).includes(
    status,
  );
}

/**
 * SPEC §3.2: "STAGED → MAPPING → VALIDATING → READY are all user-present
 * interactions"; §3.1's `cancelRequested` and §3.2's "any pre-COMMITTING state
 * → CANCELLED (clean — nothing was written)" both key off this set. §6.1
 * renders exactly these four as the "Draft" badge.
 */
export const IMPORT_JOB_PRE_COMMIT_STATUSES = [
  'STAGED',
  'MAPPING',
  'VALIDATING',
  'READY',
] as const satisfies readonly ImportJobStatus[];

// ---------------------------------------------------------------------------
// §3.1 — job type
// ---------------------------------------------------------------------------

/**
 * SPEC §3.1 `type`: "`contacts` in V1. Future: `transactions`,
 * `subscriptions`."
 *
 * V1 ships the one-value enum rather than a bare string: an enum makes adding
 * the future values a visible, reviewable contract change.
 */
export const importJobTypeSchema = z.enum(['contacts']);
export type ImportJobType = z.infer<typeof importJobTypeSchema>;

// ---------------------------------------------------------------------------
// §3.1 — the `counts` map
// ---------------------------------------------------------------------------

/**
 * SPEC §3.1 `counts`: "`{ total, created, merged, excluded, errored }` — the
 * canonical outcome vocabulary (§3.3.1). Invariant:
 * `total = created + merged + excluded + errored`."
 *
 * Note the field is `errored`, NOT `errors` — §6.2 prose says "processed =
 * new + merged + excluded + errors", but that sentence is describing UI
 * arithmetic, and §3.1's field table plus §3.3.1's "increments
 * `counts.errored`" are the normative spelling. Likewise `created` is the
 * stored name even though §3.1 notes "the Hub renders `created` as 'new'" —
 * the UI label is a rendering concern, not the contract.
 *
 * All five are non-negative integers. `total` is known once validation has
 * run; the other four start at 0 and are advanced by atomic DDB `ADD`.
 */
export const importJobCountsSchema = z.object({
  total: z.number().int().nonnegative(),
  created: z.number().int().nonnegative(),
  merged: z.number().int().nonnegative(),
  excluded: z.number().int().nonnegative(),
  errored: z.number().int().nonnegative(),
});
export type ImportJobCounts = z.infer<typeof importJobCountsSchema>;

/**
 * SPEC §3.1's stated invariant, as a callable predicate so the completion
 * screen and the reconciliation test assert the same thing.
 *
 * ⚠️ SCOPE — this holds at SUCCESS, not at every status. §3.1 words the
 * invariant as "every row is accounted for and **the completion screen**
 * reconciles", and that qualifier is load-bearing:
 *
 *   - mid-COMMITTING, the four outcome counts are still climbing toward
 *     `total` (they are advanced by atomic `ADD` per batch), so the sum is
 *     legitimately less than `total`;
 *   - on FAILED / CANCELLED, §3.2 says the rollback SUCCEEDED — nothing
 *     remains written — so the outcome counts are 0 while `total` still
 *     records how many rows the file had;
 *   - on FAILED_NEEDS_CLEANUP, partial data exists by definition and the
 *     counts are whatever the failed run reached.
 *
 * So callers must NOT assert this on an arbitrary job. Use
 * `countsShouldReconcile(status)` to decide whether the assertion applies.
 * Asserting it unconditionally would fire on perfectly healthy in-flight jobs.
 *
 * ⚠️ UNRESOLVED SPEC TENSION — flagged, not silently decided. §6.1's counts row
 * says the UI "asserts the §3.1 counts invariant …; a mismatch renders as a
 * visible error state, never silently," with no status qualifier, while §3.1
 * scopes the invariant to "the completion screen." Read literally, §6.1 would
 * have E1 render an error state on every healthy COMMITTING job and on every
 * rolled-back FAILED/CANCELLED job. This helper takes the §3.1 reading.
 *
 * The case that actually matters and is NOT settled by either reading:
 * FAILED_NEEDS_CLEANUP, where partial data genuinely exists and a
 * reconciliation mismatch is the most diagnostic signal available — arguably
 * exactly where the Hub SHOULD surface it. Resolve before E1 implements the
 * counts row (it is a §6.1 wording fix either way).
 */
export function countsReconcile(counts: ImportJobCounts): boolean {
  return (
    counts.total ===
    counts.created + counts.merged + counts.excluded + counts.errored
  );
}

/**
 * Whether §3.1's reconciliation invariant is expected to hold for a job in
 * this status — see the scope note on `countsReconcile`.
 *
 * SUCCESS is the completion state §3.1 speaks about. REVERSING/REVERSED
 * descend from SUCCESS and do not rewrite `counts` (reversal outcomes go to
 * the separate `reversal` map, §3.1), so the original completion counts must
 * still reconcile there.
 */
export function countsShouldReconcile(status: ImportJobStatus): boolean {
  return status === 'SUCCESS' || status === 'REVERSING' || status === 'REVERSED';
}

// ---------------------------------------------------------------------------
// §3.1 — the `consentAttestation` map
// ---------------------------------------------------------------------------

/**
 * SPEC §3.1 `consentAttestation`: "`{ attested, actorId, timestamp,
 * copyVersion }` — `attested: false` is a valid recorded outcome (declining
 * still imports, §9.1); `copyVersion` pins which attestation text the actor
 * saw."
 *
 * `attested` is therefore a real boolean, NOT "present means true" — a
 * consumer must render the false case, not treat it as missing.
 */
export const consentAttestationSchema = z.object({
  attested: z.boolean(),
  actorId: z.string(),
  timestamp: z.string().datetime(),
  copyVersion: z.string(),
  /**
   * The SEPARATE SMS attestation (`decisions.md#D-SMS-OPT-IN-GRANT`).
   *
   * A DISTINCT FIELD, not a reuse of `attested`, because the two claims are not
   * the same claim and are not made under the same law. `attested` is the
   * CAN-SPAM email posture — "we have a relationship with these people" — and it
   * legitimately supplies email consent for rows with no consent column. This
   * one is the TCPA claim: express written consent to receive TEXTS, per
   * contact, producible on request. Collapsing them would make ticking one box
   * grant both, which is exactly the coercion `SMS_CONSENT_VALUES`' shape used
   * to prevent.
   *
   * ⚠️ THIS PERMITS A GRANT; IT NEVER CREATES ONE. `true` here does nothing on
   * its own — a row still needs an explicit per-row SMS opt-in column value. A
   * file with no SMS column grants nothing no matter what this says. See
   * `SMS_CONSENT_VALUES` for why that asymmetry with email is load-bearing.
   *
   * OPTIONAL, and absent means NOT ATTESTED. Every job record written before
   * this field existed omits it, and those imports were run under the
   * never-grant posture — reading absence as "no SMS attestation" is both
   * backwards-compatible and the honest reading.
   */
  smsAttested: z.boolean().optional(),
  /**
   * Which SMS attestation wording the actor saw. Separate from `copyVersion`
   * for the same reason the booleans are separate: the two texts version
   * independently, and "which words did they agree to" is the question the
   * record has to answer years later.
   */
  smsCopyVersion: z.string().optional(),
});
export type ConsentAttestation = z.infer<typeof consentAttestationSchema>;

// ---------------------------------------------------------------------------
// §3.1 — the `reversal` map
// ---------------------------------------------------------------------------

/**
 * SPEC §3.1 `reversal`: "`{ requestedAt, actorId, deleted, retained,
 * completedAt }` — populated by the reversal workflow (§3.5)."
 *
 * `deleted`/`retained` are counts (§6.3: "the `reversal` map records deleted
 * vs. retained counts"). `completedAt` is absent while the job is REVERSING
 * and present at REVERSED — the map is written at request time, then
 * completed.
 */
export const importJobReversalSchema = z.object({
  requestedAt: z.string().datetime(),
  actorId: z.string(),
  /**
   * §3.5 writes these when the reversal FINALIZES, so they are absent — not
   * zero — while the job is REVERSING. That distinction is load-bearing: a
   * required `0` renders as "0 deleted, 0 retained", i.e. "the reversal found
   * nothing", which is a different and wrong statement from "we don't know
   * yet". Consumers should key "is the reversal finished?" off `completedAt`.
   */
  deleted: z.number().int().nonnegative().optional(),
  retained: z.number().int().nonnegative().optional(),
  /** Absent while REVERSING; set when the reversal finishes (§3.2). */
  completedAt: z.string().datetime().optional(),
});
export type ImportJobReversal = z.infer<typeof importJobReversalSchema>;

// ---------------------------------------------------------------------------
// §3.1 — the job record itself
// ---------------------------------------------------------------------------

/**
 * The `SK = "JOB"` record. Field order matches SPEC §3.1's table exactly so
 * the two can be diffed line by line.
 *
 * `mappingConfig` is typed as the §5.1 mapping-config shape re-exported from
 * `./mapping.js` — SPEC §3.1 says "shape defined in §5.1", so it is a real
 * reference rather than a loose Map.
 */
export const importJobSchema = z.object({
  /** PK. Client-generated UUID; doubles as the create idempotency key. */
  jobId: z.string(),
  /**
   * SK. Always the literal `"JOB"` for this record type (§3.1).
   *
   * Carried on the schema, not just as an exported constant: zod strips
   * unknown keys, so a DDB item round-tripped through `importJobSchema.parse`
   * would silently LOSE its sort key if the field were absent — and a
   * write-back of that parsed object would be a malformed item.
   */
  recordType: z.literal(JOB_RECORD_TYPE),
  /**
   * The org running the import (§3.1). Note this is the LOGICAL field name; the
   * GSI's physical key attribute is `gsi1pk` — see `importJobItemSchema` below.
   */
  orgId: z.string(),
  /** When the job was created. Sorts the Hub listing newest-first (§3.1). */
  createdAt: z.string().datetime(),
  /** Clerk user ID of whoever started the import. */
  actorId: z.string(),
  /** Display name, denormalized at create time so the Hub needs no Clerk lookup. */
  actorName: z.string(),
  /**
   * The org's NAME STRING, denormalized at create time (PE-1982) — the same
   * shape as `actorName` above, for a different reason.
   *
   * This is the `merchantName` half of the email-lookup key
   * `{normalized_email}-{merchantName}`, which SPEC §3.4 rule 1 makes BINDING:
   * the key is derived from the org's name, never its id, so the TS rails
   * produce a byte-identical key to the Python writer and the two writers'
   * conditional writes contend on the SAME item. Nothing resolved it, so every
   * imported contact keyed on `{email}-`.
   *
   * OPTIONAL on the schema, REQUIRED in practice: jobs created before PE-1982
   * do not carry it, and zod would strip an undeclared attribute on read.
   * The commit path refuses a job without it rather than falling back to `''`
   * — that fallback IS the defect.
   */
  merchantName: z.string().optional(),
  /**
   * The CharityStack org id (`M_*` / `org_*`), denormalized alongside
   * `merchantName` from the same org read (PE-1982).
   *
   * NOT the Clerk org id. This is what Python stamps as a contact's
   * `merchantID`, and it is the ContactDirtyIndex partition key the marketing
   * PocketFlows drain queries — so the Clerk id here mis-partitions every
   * imported contact for that drain. SPEC §3.4 rule 1's parenthetical
   * describing `merchantID` as "the Clerk UUID" is wrong and is corrected in
   * the same change.
   */
  merchantId: z.string().optional(),
  type: importJobTypeSchema,
  status: importJobStatusSchema,
  /** Original uploaded filename, shown in the Hub. */
  fileName: z.string(),
  /** S3 key of the original uploaded file. */
  sourceFileKey: z.string(),

  /**
   * The MOST RECENTLY MINTED source-table key (O6), written by
   * `jobs.presignSourceTable`. ADM-35; additive per annex §8.
   *
   * WHAT IT IS FOR. Every upload gets a fresh, write-once
   * `source-{nonce}.jsonl`, which stops an in-flight validate from having its
   * object overwritten. That alone does NOT stop a caller from naming an OLDER
   * object: they live 30 days, and a superseded table passes every other check
   * — its header line carries the right orgId/jobId, and its `headers` are
   * compared against a value from the SAME request, so unchanged headers agree
   * trivially. `validate` therefore requires the key it is given to be THIS
   * field (or, for a retried job, the inherited `sourceFileKey`), which is what
   * turns the nonce from advisory into load-bearing.
   *
   * Optional because it is absent on every job created before ADM-35 and on any
   * job that has not yet presigned an upload.
   */
  sourceTableKey: z.string().optional(),

  /**
   * Last write to this job record, in ISO form. ADDITIVE to SPEC §3.1's table,
   * which does not list it — and it is load-bearing rather than convenience.
   *
   * §6.1 mandates that drafts auto-cancel "after 30 days of INACTIVITY",
   * implemented lazily inside reads. But §3.1 defines no last-update
   * timestamp: `createdAt` measures AGE, not inactivity, and `lastProgressAt`
   * is stamped only by batch steps, which run post-commit and therefore never
   * for a draft. Without this field the expiry sweep can only measure age, and
   * a user who uploads on day 1 and is still mapping on day 31 loses the job
   * mid-wizard.
   *
   * Every write to a DRAFT job must stamp it (create, confirmMapping,
   * validate). A write that forgets makes the job look idle and expires it out
   * from under an active user.
   */
  updatedAt: z.string().datetime().optional(),

  /**
   * The per-file role signal (§4.1, annex §4.1b). ADDITIVE to §3.1's table.
   *
   * §4.1 says it is "stored on the job record" and that "upload is the only
   * place to catch it" — some platforms encode consent in WHICH FILE a row
   * came from rather than in a column (a Mailchimp export is a ZIP of separate
   * subscribed/unsubscribed/cleaned/pending files, none of which has a consent
   * column). §3.1's field table simply omits it, and it cannot live only in
   * `mappingConfig`: that is absent until the user confirms a mapping, which
   * is a whole step after the only moment the signal is recoverable.
   */
  sourceFileHint: sourceFileHintSchema.optional(),

  /**
   * The optional "how many contacts are you expecting?" number from the upload
   * screen (§4.0). ADDITIVE to §3.1's table.
   *
   * §4.0 calls it "the only defence that works when the damage happened
   * OUTSIDE our system" — a truncated export that parses cleanly looks
   * identical to a complete one, and only a human-supplied expectation catches
   * it. Validation compares it to the parsed row count and flags a material
   * shortfall before commit, so it must survive from upload to validate: i.e.
   * it must be on the job record.
   */
  expectedRowCount: z.number().int().nonnegative().optional(),

  // --- fields written by a later stage than create -------------------------

  /**
   * Absent until the user confirms a mapping (READY onward, and re-written
   * when they edit it). §3.1: "The confirmed column→field mapping (shape
   * defined in §5.1)."
   */
  mappingConfig: mappingConfigSchema.optional(),
  /**
   * S3 key of the CURRENT mapped dataset (SPEC §3.1, §3.7) — `mapped.jsonl`,
   * the write path's entire input.
   *
   * This field is why the write path can find its input at all. Do NOT derive
   * the key by convention (`{orgId}/{jobId}/mapped.jsonl`): SPEC §3.2 permits
   * READY → MAPPING → VALIDATING → READY, and a re-validate writes a **new**
   * key (`mapped-2.jsonl`, …) and repoints this field rather than overwriting
   * in place — overwriting would race a commit already in flight. A derived
   * key would silently read the superseded file and import the mapping the
   * user just replaced.
   *
   * Absent until the validate step has produced a dataset (READY onward).
   */
  mappedFileKey: z.string().optional(),
  /**
   * Absent until validation has produced a total. Present from VALIDATING
   * onward and advanced by every batch step.
   */
  counts: importJobCountsSchema.optional(),
  /**
   * The O4 `TriageSummary` as of the last validate (SPEC §3.1, §3.8).
   *
   * Persisted on the job record — not merely emitted as client telemetry —
   * because `decisions.md#D-FUNNEL-TRANSPORT` makes this record the funnel's
   * source of truth: a dropped analytics event must cost a dashboard, never
   * the ≥85% completion metric.
   *
   * Typed permissively on purpose. The write path only persists and echoes
   * this value; the authority on its shape is the validate step (Milestone C),
   * which also owns the `ValidationCode` vocabulary inside `issues[]`. Pinning
   * a strict shape here would force B to guess codes it does not produce, and
   * the annex (§8) requires consumers to tolerate unknown codes rather than
   * crash on them.
   */
  triage: z.record(z.string(), z.unknown()).optional(),
  /** Recorded at COMMIT (§4.4 step 1). Absent on drafts. */
  consentAttestation: consentAttestationSchema.optional(),
  /**
   * §3.1: "Set by the cancel API during COMMITTING; every batch step checks
   * it before writing." Absent until a cancel is requested — consumers must
   * treat absent as false rather than requiring the flag.
   */
  cancelRequested: z.boolean().optional(),
  /**
   * When a DRAFT was discarded from the Hub. ADDITIVE to SPEC §3.1's table.
   *
   * A MARKER, NOT A STATE — the same shape as `cancelRequested` above, and for
   * the same reason. A discarded draft *is* cancelled: it reaches CANCELLED by
   * §3.2's ordinary pre-commit transition, and nothing about its lifecycle is
   * new. What is new is a VISIBILITY question ("should the Hub list this?"),
   * which is not a lifecycle question and must not become a 14th status —
   * §3.2's enum is duplicated in the backend's state machine and guarded by a
   * conformance test, so a new state would ripple through the transition map,
   * the total-Record badge table, and every status grouping to express
   * something none of them are about.
   *
   * ⚠️ SET ONLY ON THE PRE-COMMIT PATH. `jobs.cancel` serves BOTH the Hub's
   * Discard (on a draft) and its Cancel (on a running COMMITTING import). Only
   * the first may stamp this. Stamping it on the `cancel-requested` branch
   * would hide a job whose contacts are still being written — the user would
   * lose sight of a live import at the exact moment it matters most.
   *
   * WHAT ACTUALLY HIDES THE ROW IS NOT THIS FIELD. `jobs.list` queries the
   * GSI, so hiding is implemented by REMOVING `gsi1pk`/`gsi1sk` (see
   * `importJobItemSchema`), which drops the item from the index itself. This
   * field is the human-readable record of why — an operator reading a delisted
   * item needs to know it was discarded rather than corrupted.
   */
  discardedAt: z.string().datetime().optional(),
  /** §3.1: "Populated on FAILED / FAILED_NEEDS_CLEANUP." */
  failureReason: z.string().optional(),
  /**
   * A commit attempt that failed BEFORE the workflow claimed the job
   * (PE-1986 / QA F-10).
   *
   * DISTINCT FROM `failureReason`, deliberately. That field is contractually
   * "populated on FAILED / FAILED_NEEDS_CLEANUP" and the Hub reads it as the
   * epitaph of a terminal job; setting it on a live READY draft would make
   * every `failureReason`-present check mean two different things.
   *
   * The job stays READY because READY is TRUE: the failure happened before the
   * claim, so no batch ran and nothing was written (§3.3 Step 1 gives the
   * conditional COMMITTING transition to the workflow). The user can fix the
   * cause and press Commit again on the same job — there is no new job to
   * make, and `READY → FAILED` is not a legal transition anyway (§3.2).
   *
   * Cleared on the next successful claim, so a stale banner never outlives the
   * problem it describes.
   */
  lastCommitError: z
    .object({
      message: z.string(),
      at: z.string().datetime(),
    })
    .optional(),
  /** §3.1: heartbeat stamped by every batch step; feeds the §3.3.4 watchdog. */
  lastProgressAt: z.string().datetime().optional(),
  /** §3.1: S3 key of the errors/conflicts CSV, "written at completion". */
  resultsFileKey: z.string().optional(),
  /** §3.1: "When the job finished (success or failure)." */
  completedAt: z.string().datetime().optional(),
  /** §3.1: populated by the reversal workflow (§3.5). */
  reversal: importJobReversalSchema.optional(),
});
export type ImportJob = z.infer<typeof importJobSchema>;

/**
 * The job record AS STORED IN DYNAMODB — the API shape above plus the GSI's
 * physical key attributes.
 *
 * WHY THIS EXISTS. `infra/imports.ts` declares the Hub's GSI as
 * `OrgCreatedAtIndex: { hashKey: 'gsi1pk', rangeKey: 'gsi1sk' }`, following the
 * generic-projection idiom already used by `infra/marketing.ts`. SPEC §3.1
 * describes the same index logically — "GSI: `orgId` (partition) + `createdAt`
 * (sort)" — so the *stored item* carries BOTH the logical fields and the two
 * physical key attributes, which duplicate their values:
 *
 *     gsi1pk = orgId        gsi1sk = createdAt
 *
 * §3.1's "Only `JOB` records carry the GSI attributes, so manifests never
 * appear in Hub listings" is implemented by simply omitting `gsi1pk`/`gsi1sk`
 * from batch-manifest items — a DDB item missing an index's key attribute is
 * not projected into that index. That is why `importBatchManifestSchema` has no
 * GSI fields and must not gain any.
 *
 * Milestone B's jobs repository should write/parse THIS schema and hand the
 * frontend `importJobSchema` (the API shape) — the duplicated keys are a
 * storage detail no consumer should have to know. It is modelled here rather
 * than left implicit because zod STRIPS unknown keys: parsing a stored item
 * with `importJobSchema` and writing the result back would drop both GSI keys
 * and silently remove the job from every Hub listing.
 */
export const importJobItemSchema = importJobSchema.extend({
  /**
   * Physical GSI partition key — duplicates `orgId`.
   *
   * OPTIONAL BECAUSE ABSENCE IS THE DELISTING MECHANISM, not because it is
   * sometimes forgotten. A DDB item missing an index's key attribute is not
   * projected into that index, which is how batch manifests stay out of Hub
   * listings (see this schema's header) and, since the Hub gained Discard, how
   * a discarded draft is hidden: `jobs.cancel` REMOVEs both keys in the same
   * conditional write that stamps `discardedAt`.
   *
   * A live JOB record must still carry both. Writing one without them delists
   * it permanently and silently — `toImportJobItem` is the only sanctioned way
   * to build this shape, and it always derives them.
   */
  gsi1pk: z.string().optional(),
  /** Physical GSI sort key — duplicates `createdAt`. Optional for the same reason as `gsi1pk`. */
  gsi1sk: z.string().datetime().optional(),
});
export type ImportJobItem = z.infer<typeof importJobItemSchema>;

/**
 * Project a stored item down to the API shape, and assert the duplicated GSI
 * keys actually agree with their logical counterparts. A divergence means some
 * writer updated `orgId`/`createdAt` without updating the index keys — the job
 * would then be queryable under a stale org or sort position.
 *
 * ABSENT keys are not a divergence — they are a DELISTED item (a discarded
 * draft, per `gsi1pk` above), which is a legitimate state this must project
 * rather than reject. Throwing here would make a discarded job unreadable by
 * `jobs.get`, so the one action that hides a row would also destroy the
 * operator's ability to inspect it. Only keys that are PRESENT AND WRONG are
 * the corruption this check exists to catch.
 */
export function toImportJob(item: ImportJobItem): ImportJob {
  const { gsi1pk, gsi1sk, ...job } = item;
  const delisted = gsi1pk === undefined && gsi1sk === undefined;
  if (!delisted && (gsi1pk !== job.orgId || gsi1sk !== job.createdAt)) {
    throw new Error(
      `ImportJobsTable item ${job.jobId}: GSI keys disagree with logical fields ` +
        `(gsi1pk=${gsi1pk} vs orgId=${job.orgId}; gsi1sk=${gsi1sk} vs createdAt=${job.createdAt})`,
    );
  }
  return job;
}

/**
 * Build the stored item from the API shape, deriving the GSI keys.
 *
 * ALWAYS LISTED. Delisting is a targeted `REMOVE` on an existing item, never a
 * property of a freshly-built one — a job that has just been constructed has
 * not been discarded.
 */
export function toImportJobItem(job: ImportJob): ImportJobItem {
  return { ...job, gsi1pk: job.orgId, gsi1sk: job.createdAt };
}

// ---------------------------------------------------------------------------
// §3.1 — batch manifest items
// ---------------------------------------------------------------------------

/**
 * SPEC §3.1 `mergeDeltas`: "Per merged contact: `{ contactId, filledFields:
 * { field: value } }` — exactly what the import wrote onto an existing
 * contact. This is the inverse needed to revert a merge (§3.5)."
 *
 * `filledFields` values are deliberately `unknown`: the fields a merge fills
 * are contact fields of mixed type (strings, arrays of emails, address maps),
 * and narrowing them here would either be wrong or would drag the whole
 * contact shape into this contract. Milestone B's rails own that typing.
 */
export const mergeDeltaSchema = z.object({
  contactId: z.string(),
  filledFields: z.record(z.string(), z.unknown()),
});
export type MergeDelta = z.infer<typeof mergeDeltaSchema>;

/**
 * The `SK = "BATCH#....."` items — "one per batch step, immutable once
 * written" (§3.1). Read only by rollback/reversal, via a Query on the jobId
 * partition.
 */
export const importBatchManifestSchema = z.object({
  /**
   * PK — the same partition as the job record (§3.1: "everything about one job
   * lives in one partition"). Present on the item, so a manifest read out of a
   * Query is traceable back to its job.
   */
  jobId: z.string(),
  /**
   * SK — `BATCH#` + a zero-padded batch index, matching §3.1's `BATCH#00042`.
   *
   * The padding width is enforced (≥5 digits, digits only) rather than just the
   * prefix: manifests are read by Query and DDB sorts sort keys as STRINGS, so
   * an unpadded `BATCH#9` would sort before `BATCH#10` and a rollback replaying
   * batches in key order would process them out of sequence.
   */
  recordType: z
    .string()
    .regex(
      /^BATCH#\d{5,}$/,
      `must be ${BATCH_RECORD_TYPE_PREFIX} followed by a zero-padded batch index (≥5 digits)`,
    ),
  createdContactIds: z.array(z.string()),
  mergeDeltas: z.array(mergeDeltaSchema),
  writtenAt: z.string().datetime(),
});
export type ImportBatchManifest = z.infer<typeof importBatchManifestSchema>;
